/**
 * Orchestrates the save-confirmation dialog for direct interview edits.
 *
 * - Captures a change snapshot once when edit data is hydrated (prefillVersion > 0).
 * - `requestSave(onConfirmed)` computes the diff and either:
 *     • calls onConfirmed immediately (no changes, or not an edit, or self-schedule)
 *     • opens the dialog and defers onConfirmed until the user confirms
 * - Dialog is only shown for `isEdit && !isSelfSchedule`.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { MutableRefObject } from 'react';
import type {
  ScheduleInterviewPayload,
  InterviewUpdateFlags,
} from '../config/schedule.types';
import { buildChangeSnapshot } from './buildChangeSnapshot';
import {
  computeUpdateFlags,
  type SaveConfirmationVariant,
} from './useChangeTracking';

const NO_FLAGS: InterviewUpdateFlags = {
  update_calendar_invite_to_candidate: false,
  update_calendar_invite_to_interviewer: false,
  resend_email_to_candidate: false,
  resend_email_to_interviewer: false,
};

interface UseEditConfirmationArgs {
  payloadRef: MutableRefObject<ScheduleInterviewPayload>;
  prefillVersion: number;
  isEdit: boolean;
  isSelfSchedule: boolean;
}

export interface EditConfirmationState {
  /** Intercepts save — shows dialog when needed, calls onConfirmed directly otherwise. */
  requestSave: (_onConfirmed: (_flags: InterviewUpdateFlags) => void) => void;
  /** Call when the user clicks "Save Interview" in the dialog. */
  confirmDialog: (_note: string) => void;
  /** Call when the user clicks "Cancel" in the dialog. */
  cancelDialog: () => void;
  /** Non-null when dialog should be rendered. */
  dialogVariant: SaveConfirmationVariant;
  isDialogOpen: boolean;
  /** Flags computed for the pending save — used to filter which recipients to show in the dialog. */
  dialogFlags: InterviewUpdateFlags;
}

export function useEditConfirmation({
  payloadRef,
  prefillVersion,
  isEdit,
  isSelfSchedule,
}: UseEditConfirmationArgs): EditConfirmationState {
  const originalSnapshot = useRef(buildChangeSnapshot(payloadRef.current));
  const snapshotCaptured = useRef(false);
  const pendingSave = useRef<((_flags: InterviewUpdateFlags) => void) | null>(
    null
  );

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogVariant, setDialogVariant] =
    useState<SaveConfirmationVariant>(null);
  const [pendingFlags, setPendingFlags] =
    useState<InterviewUpdateFlags>(NO_FLAGS);

  // Capture the original snapshot once when edit hydration fires.
  useEffect(() => {
    if (prefillVersion > 0 && !snapshotCaptured.current) {
      snapshotCaptured.current = true;
      originalSnapshot.current = buildChangeSnapshot(payloadRef.current);
    }
  }, [prefillVersion, payloadRef]);

  const requestSave = useCallback(
    (onConfirmed: (_flags: InterviewUpdateFlags) => void) => {
      if (!isEdit || isSelfSchedule) {
        onConfirmed(NO_FLAGS);
        return;
      }

      const current = buildChangeSnapshot(payloadRef.current);
      const { flags, variant } = computeUpdateFlags(
        originalSnapshot.current,
        current
      );

      if (!variant) {
        onConfirmed(NO_FLAGS);
        return;
      }

      pendingSave.current = onConfirmed;
      setPendingFlags({ ...flags });
      setDialogVariant(variant);
      setIsDialogOpen(true);
    },
    [isEdit, isSelfSchedule, payloadRef]
  );

  const confirmDialog = useCallback(
    (note: string) => {
      const finalFlags = {
        ...pendingFlags,
        ...(note ? { note_to_guests: note } : {}),
      };
      setIsDialogOpen(false);
      const save = pendingSave.current;
      pendingSave.current = null;
      save?.(finalFlags);
    },
    [pendingFlags]
  );

  const cancelDialog = useCallback(() => {
    pendingSave.current = null;
    setIsDialogOpen(false);
  }, []);

  return {
    requestSave,
    confirmDialog,
    cancelDialog,
    dialogVariant,
    isDialogOpen,
    dialogFlags: pendingFlags,
  };
}
